import Prof from "@didi/mfe-global-utils/lib/prof";
import { preserveQuery, useRouter } from "~/utils/router";
import { useStore } from "./store";
import {
  CANONICAL_COUNTRY_CODE,
  COUNTRY_CALLING_CODE,
  COUNTRY_ID,
} from "~/constants/common";
import { lang } from "./lang";
import Cookies from "js-cookie";
import { getCookie } from "./cookie";
import { isMarket, isFromMarKeTing } from "./scene";
import { trackEvent } from "@/utils/register/omega";
import { querySet } from "./url";
import { setConfig } from "@didi/error-base";
import log from '@/utils/log'

const syncErrorBaseUserId = (uid?: string | number) => {
  setConfig({
    userId: uid ? String(uid) : "",
  });
};
/**
 * cookie过期时间 单位天
 */
const DEFAULT_EXPIRES = 1;

const cookieOpt = {
  domain:
    process.env.NODE_ENV === "production" || process.env.NODE_ENV === "preview"
      ? location.host
      : "",
  path: "/",
  expires: DEFAULT_EXPIRES,
};

/**
 * 设置外部token的Cookie
 * @param token - 需要设置的token值
 */
export function setCookie(token: string) {
  // 使用Cookies.set方法设置名为'external_token'的Cookie
  // 参数包括token值和预设的cookie配置选项
  Cookies.set("external_token", token, cookieOpt);
}

/**
 * passport国际化版本映射对象
 */
const loginLibs = {
  // 生产环境
  production:
    "//static.didiglobal.com/common-biz/global-login/2.2.6/login.global.min.js",
};

const PASSPORT_URL = loginLibs["production"];

/**
 * 将token传递给passport SDK
 * 用于在非营销场景下，将cookie中的token同步到passport SDK中
 * @param token - 需要设置的token值
 * @returns Promise<string> - 成功返回'success'，失败返回reject('error')
 */
export const setTicket = (token: string) => {
  return new Promise((resolve, reject) => {
    window.login?.setTicket(
      token,
      () => {
        resolve("success");
      },
      () => {
        reject("error");
      }
    );
  });
};

export const requireRemote = Prof.requireRemote;

/**
 * 登录处理函数：若用户未登录,跳转登录页；若用户已登录，执行回调，去往首页
 */
export const handleLogin = async () => {
  // 优先检测cookie中的token 过期时间1天
  const token = Cookies.get("external_token") || getCookie("ticket");
  if (!token) {
    useStore().commit("user/update", {
      uid: "",
      phone: "",
      token: "",
    });
    useRouter()?.replace(
      preserveQuery({
        name: "login",
      })
    );
    return Promise.reject("not login");
  }
  // cookie中有token时 再调用passport检测
  const config = {};
  // 如果是外投场景 需要给passport提供额外参数
  if (isMarket()) {
    const mediaPassportInfo = window.adv.getAdvExtraInfoForPassport();
    Object.assign(config, mediaPassportInfo);
  }
  if (!window.login) {
    try {
      await initPassport(config);
      console.log("init passport success");
    } catch (err) {
      log.error("init passport error", { err });
      throw err
    }
  }
  return new Promise((resolve, reject) => {
    // 用户token过期触发强制登陆，isLogin为true时表示用户已登录
    window.login?.isLogin(
      (res: { login: boolean; ticket: string; cell: number; uid: number }) => {

        const { login, ticket, cell, uid } = res;
        if (login) {
          useStore().commit("user/update", {
            uid,
            phone: cell,
            token: ticket,
          });
          syncErrorBaseUserId(uid);
          // 登录成功后更新omega公参uid 同时最外层也上报uid	
          window.Omega?.setConfig({	
            userId: uid,	
            attrs: {	
              uid: uid,	
            }	
          })
          setCookie(ticket)
          resolve("has logined");
        } else {
          //登陆过期，先清除store中的数据
          syncErrorBaseUserId();
          useStore().commit("user/update", {
              uid: "",
              phone: "",
              token: "",
            });
            // 跳转到自定义登陆
            useRouter()?.replace(
              preserveQuery({
                name: "login",
                query:{
                  expired_source:'expired'
                }
              })
            );
            resolve("Go to login");
        }
      },
      (err: any) => {
         //登陆过期，先清除store中的数据
        syncErrorBaseUserId();
        useStore().commit("user/update", {
          uid: "",
          phone: "",
          token: "",
        });
        // 跳转到自定义登陆
        useRouter()?.replace(
          preserveQuery({
            name: "login",
            query:{
              expired_source:'expired'
            }
          })
        );
        reject("passport sdk error");
      }
    );
  });
};

export const relogin = async() => {
  // 用户登录过期 重新唤起登录
  try {
    await handleLogin()
  } catch (err) {
    log.error('relogin error', { err })
  }
};

/**
 * 检测是否登录，没登录不做后续处理，已登录仅更新登录信息，也不做跳转
 */
export const checkLoginStatus = async () => {
    trackEvent('tech_credit_outside_sw', {
        event_name: 'tech_login_status_check_start'
    })
  // 优先检测cookie中的token 过期时间1天
  const token = Cookies.get("external_token") || getCookie("ticket");
  // console.log('token: ',token)
  const uidForMarket = Cookies.get("uid");
  const phoneForMarket = Cookies.get("cell");
  if (!token) {
      trackEvent('tech_credit_outside_sw', {
          event_name: 'tech_login_status_not_login'
      })
    return Promise.reject("not login");
  }

  // 如果不是从营销页面打开，则需要初始化passport并设置ticket
  const isFromMarketing = isFromMarKeTing();
  if (!isFromMarketing) {
    // 如果不是从营销页面打开
    if (!window.login) {
      await initPassport();
    }
    await setTicket(token); // 设置ticket
  }

  return new Promise((resolve, reject) => {
    if (token && isFromMarketing) {
      useStore().commit("user/update", {
        uid: uidForMarket,
        phone: phoneForMarket,
        token: token,
      });
      syncErrorBaseUserId(uidForMarket);
      useStore().commit("user/update", {
        marketFreeLogin: true,
      });
        trackEvent('tech_credit_outside_sw', {
            event_name: 'tech_login_status_has_login'
        })
      resolve("has logined");
    }
    window.login?.isLogin(
      (res: { login: boolean; ticket: string; cell: number; uid: number }) => {
        const { login, ticket, cell, uid } = res;
        if (login) {
          useStore().commit("user/update", {
            uid,
            phone: cell,
            token: ticket,
          });
          syncErrorBaseUserId(uid);
          if (getCookie("ticket") && isFromMarKeTing()) {
            // 外投免登 记录免登标识
            useStore().commit("user/update", {
              marketFreeLogin: true,
            });
          }
            trackEvent('tech_credit_outside_sw', {
                event_name: 'tech_login_status_has_login'
            })
          resolve("has logined");
        } else {
            syncErrorBaseUserId();
            trackEvent('tech_credit_outside_sw', {
                event_name: 'tech_login_status_not_login'
            })
          reject("not login");
        }
      },
      (err: any) => {
          syncErrorBaseUserId();
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_login_status_passport_error'
          })
        reject("passport sdk error");
      }
    );
  });
};

const defaultLoginConf = {
  appid: 200112,
  role: 1,
  country_calling_code: COUNTRY_CALLING_CODE,
  country_id: COUNTRY_ID,
  canonical_country_code: CANONICAL_COUNTRY_CODE,
};

/**
 * 初始化passport sdk
 * @param config passport-sdk初始化所需参数
 * @returns
 */
export const initPassport = (config = {}) => {
  const marketConfig = {};
    trackEvent('tech_credit_outside_sw', {
        event_name: 'tech_passport_init_start'
    })
  // 如果是外投场景 需要给passport提供额外参数
  if (isMarket()) {
    const mediaPassportInfo = window.adv.getAdvExtraInfoForPassport();
    Object.assign(marketConfig, mediaPassportInfo);
  }
  const conf = {
    ...defaultLoginConf,
    lang,
    ...config,
    extra_info: {
      ...marketConfig,
    },
  };
  return new Promise((res, rej) => {
    const bootstrapPassport = async () => {
    if (window.login && window.login.setConfig) {
      try {
        window.login.setConfig(conf);
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_passport_init_success'
          })
        res("success");
      } catch (err) {
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_passport_init_failed'
          })
        rej(err);
      }
    } else {
      const timeout = setTimeout(() => {
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_passport_init_timeout'
          })
        rej(new Error("passport sdk 加载超时"));
      }, 10000);

      requireRemote(
        {
          src: PASSPORT_URL,
          async: true,
        },
        () => {
          clearTimeout(timeout);
          if (window.login && window.login.setConfig) {
            try {
              window.login.setConfig(conf);
                trackEvent('tech_credit_outside_sw', {
                    event_name: 'tech_passport_init_success'
                })
              res("success");
            } catch (err) {
                trackEvent('tech_credit_outside_sw', {
                    event_name: 'tech_passport_init_failed'
                })
              rej(err);
            }
          } else {
              trackEvent('tech_credit_outside_sw', {
                  event_name: 'tech_passport_init_failed'
              })
            rej(new Error("passport sdk 加载失败，window.login 不存在"));
          }
        }
      );
    }
    };

    bootstrapPassport().catch((error) => {
      trackEvent('tech_credit_outside_sw', {
          event_name: 'tech_passport_init_failed'
      })
      rej(error);
    });
  });
};

type TPassportSuccessReturn = {
  ticket: string;
  cell: string;
  uid: number;
  uid_str: string;
  suid: number;
};

type TPassportLoginError = {
  message: string
  code: string | number
  passportVerifTracked: boolean
}

const formatTrackErrorMsg = (msg: any): string => {
  if (typeof msg === 'string') {
    const raw = msg.trim()
    if (!raw) return ''
    try {
      const parsed = JSON.parse(raw)
      return formatTrackErrorMsg(parsed)
    } catch (e) {
      return raw
    }
  }
  if (msg === null || msg === undefined) return ''
  if (typeof msg === 'number' || typeof msg === 'boolean') {
    return String(msg)
  }
  if (typeof msg === 'object') {
    const preferred =
      msg.error ??
      msg.errorMsg ??
      msg.msg ??
      msg.message
    if (preferred !== undefined) {
      return formatTrackErrorMsg(preferred)
    }
    try {
      return JSON.stringify(msg)
    } catch (e) {
      return '[object Object]'
    }
  }
  return String(msg)
}

/**
 * 用户输入完手机号, 去登录（验证码/密码）
 */
export const userLogin = async (phone: string) => {
    trackEvent('tech_credit_outside_sw', {
        event_name: 'tech_user_login_start'
    })
  if (!window.login) {
    await initPassport();
  }

  return new Promise((resolve, reject) => {
    const handleSuccess = (res: TPassportSuccessReturn) => {
      // res增加校验 数据不可靠
      if (res.uid && res.ticket && res.cell) {
        trackEvent('fin_credit_abroad_sign_phone_passport_verif_bt', {
          result: 1,
          error_msg: '',
          error_code: ''
        })
        useStore().commit("user/update", {
          uid: res.uid,
          token: res.ticket,
          phone: res.cell,
        });
        syncErrorBaseUserId(res.uid);
        // 登录成功后更新omega公参uid 同时最外层也上报uid
        window.Omega?.setConfig({
          userId: res.uid,
          attrs: {
            uid: res.uid,
          },
        });
        setCookie(res.ticket);
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_user_login_success'
          })
        resolve(res);
      } else {
        const errorMsg = "已经登录但未获取到用户信息"
        console.error("已经登录但未获取到用户信息");
        trackEvent('fin_credit_abroad_sign_phone_passport_verif_bt', {
          result: 0,
          error_msg: errorMsg,
          error_code: ''
        })
          trackEvent('tech_credit_outside_sw', {
              event_name: 'tech_user_login_failed'
          })
        reject({
          message: errorMsg,
          code: '',
          passportVerifTracked: true,
        } as TPassportLoginError);
      }
    };
    const handleError = (err?: any) => {
        const rawErrorMsg =
          err?.message ??
          err?.msg ??
          err?.errorMsg ??
          err?.response?.data?.errorMsg ??
          err?.response?.data?.message ??
          err
        const errorMsg = formatTrackErrorMsg(rawErrorMsg) || 'unknown_error'
        const rawErrorCode =
          err?.code ??
          err?.errorCode ??
          err?.response?.data?.errorCode ??
          err?.response?.data?.code
        const errorCode =
          rawErrorCode === null || rawErrorCode === undefined || rawErrorCode === ''
            ? ''
            : String(rawErrorCode)
        trackEvent('fin_credit_abroad_sign_phone_passport_verif_bt', {
          result: 0,
          error_msg: errorMsg,
          error_code: errorCode
        })
        trackEvent('tech_credit_outside_sw', {
            event_name: 'tech_user_login_failed'
        })
      console.log("注册失败");
      reject({
        message: errorMsg || "登录失败",
        code: errorCode,
        passportVerifTracked: true,
      } as TPassportLoginError);
    };
    window.login.checkPhone(phone, handleSuccess, handleError);
  });
};

/*
退出登录
*/

export const autoLogout = () => {
  return new Promise((resolve, reject) => {
    const logout = () => {
      window.login.logout(() => {
        resolve('success')
      }, () => {
        reject('fail')
      })
    }
    if (window.login && window.login.logout) {
      logout()
    } else {
      initPassport().then(() => {
        logout()
      }).catch(() => {
        reject('fail')
      })
    }
  })
}

export const LogInAgain = async () => {
  try {
    await autoLogout()
    await handleLogin()
    return {
      success: true,
      msg: 'Log in again'
    }
  } catch (err) {
    log.error('log_in_again', { err })
    throw err
  }
}
